import { useMemo, useState, type ReactNode } from "react";
import {
  commentPinWorldPoint,
  resolveCommentPin,
  scanPlanRooms,
  type PlanRoom,
  type ScanRoomGroup,
} from "@sts/geometry";
import { ScanViewer, type ScanViewerPin } from "@sts/scan-viewer";
import type { PlanCommentThread, PlanObject } from "../api/types.js";
import { commentObjects, commentTargetLabel, commentWhen } from "./plan-comments.js";
import { usePlanComments, type PlanCommentsSource } from "./use-plan-comments.js";
import { usePlanObjects, type PlanObjectsSource } from "./use-plan-objects.js";

/**
 * The 3D model with the plan's OPEN comments pinned in it (migration 0043).
 *
 * Every pin is the plan's own, stood up in the model: the same point on the
 * plan (following its wall, window, room or object as the plan does), at the
 * height it was dropped at, else half way up its wall, else 1.2 m above the
 * floor (`commentPinWorldPoint`). Comments are written and answered on the
 * plan; here a click on a pin shows what it says.
 */

export interface CommentPin3d {
  readonly thread: PlanCommentThread;
  readonly pin: ScanViewerPin;
}

/** The open threads' pins in world space. A pin with no height to stand at (no floor found) is left out. */
export function commentPins3d(
  threads: readonly PlanCommentThread[],
  rooms: readonly PlanRoom[],
  objects: readonly PlanObject[],
): CommentPin3d[] {
  const followable = commentObjects(objects);
  return threads.flatMap((thread) => {
    if (thread.status !== "open") return [];
    const storeyRooms = rooms.filter((room) => room.story === thread.pin.storey);
    const placed = resolveCommentPin(
      thread.pin,
      storeyRooms,
      followable.filter((o) => o.storey === thread.pin.storey),
    );
    const position = commentPinWorldPoint(thread.pin, placed, storeyRooms);
    return position ? [{ thread, pin: { id: thread.id, number: thread.number, position } }] : [];
  });
}

export function ScanViewerWithComments({
  surveyId,
  groups,
  selectedId,
  hiddenIds,
  onSelect,
  comments,
  objects,
  turnDeg = 0,
}: {
  readonly surveyId: string;
  readonly groups: readonly ScanRoomGroup[];
  /** The plan's turn, so the model stands the same way round (plan-orientation.ts). */
  readonly turnDeg?: number;
  readonly selectedId?: string;
  readonly hiddenIds: ReadonlySet<string>;
  readonly onSelect: (id: string | undefined) => void;
  readonly comments?: PlanCommentsSource;
  readonly objects?: PlanObjectsSource;
}): ReactNode {
  const state = usePlanComments(comments, surveyId);
  const planObjects = usePlanObjects(objects, surveyId);
  const rooms = useMemo(() => scanPlanRooms(groups), [groups]);
  const placed = useMemo(
    () => commentPins3d(state.threads, rooms, planObjects.objects),
    [state.threads, rooms, planObjects.objects],
  );
  const pins = useMemo(() => placed.map((p) => p.pin), [placed]);
  const [pickedId, setPickedId] = useState<string | undefined>(undefined);
  const picked = placed.find((p) => p.thread.id === pickedId)?.thread;

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }} data-testid="scan-viewer-with-comments">
      <ScanViewer
        groups={groups}
        turnDeg={turnDeg}
        {...(selectedId ? { selectedId } : {})}
        hiddenIds={hiddenIds}
        onSelect={(id) => {
          setPickedId(undefined);
          onSelect(id);
        }}
        pins={pins}
        {...(pickedId ? { selectedPinId: pickedId } : {})}
        onPinSelect={(id) => setPickedId((current) => (current === id ? undefined : id))}
      />
      {picked ? (
        <div
          role="note"
          aria-label={`Comment ${picked.number}`}
          data-testid="scan-comment-card"
          style={{
            position: "absolute",
            top: 44,
            right: 12,
            zIndex: 3,
            maxWidth: 280,
            padding: "10px 12px",
            borderRadius: 10,
            background: "rgba(17, 24, 34, 0.9)",
            color: "#f4f7fa",
            fontSize: 13,
            lineHeight: 1.35,
            boxShadow: "0 6px 24px rgba(0,0,0,0.28)",
          }}
        >
          <div style={{ fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase", opacity: 0.7 }}>
            Comment {picked.number}
            {(() => {
              const about = commentTargetLabel(picked.pin.target, rooms, planObjects.objects);
              return about ? ` · ${about}` : "";
            })()}
          </div>
          <div style={{ marginTop: 4, whiteSpace: "pre-wrap" }}>{picked.body}</div>
          <div style={{ marginTop: 6, fontSize: 12, opacity: 0.75 }}>
            {picked.author?.name || "Someone"} · {commentWhen(picked.createdAt)}
            {picked.replies.length > 0
              ? ` · ${picked.replies.length} ${picked.replies.length === 1 ? "reply" : "replies"} — open the plan to read and reply`
              : " — reply on the plan"}
          </div>
          <button
            type="button"
            onClick={() => setPickedId(undefined)}
            style={{
              marginTop: 6,
              background: "transparent",
              border: 0,
              color: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
              padding: 0,
              fontSize: 12,
            }}
          >
            Close
          </button>
        </div>
      ) : null}
      {placed.length > 0 ? (
        <p
          className="survey-scan__comment-count"
          data-testid="scan-3d-comment-count"
          style={{
            position: "absolute",
            top: 12,
            right: 12,
            zIndex: 3,
            margin: 0,
            padding: "3px 8px",
            borderRadius: 6,
            background: "rgba(255,255,255,0.88)",
            fontSize: 12,
            pointerEvents: "none",
          }}
        >
          {placed.length} open {placed.length === 1 ? "comment" : "comments"} pinned in the model
        </p>
      ) : null}
    </div>
  );
}
